import { z } from 'zod';

/** Timestamp wire-compatible with node-postgres rows and JSON responses. */
const wireTimestamp = z.union([z.string(), z.date()]);

/**
 * Task #2616 — Contratti di risposta tipizzati per gli endpoint del catalogo POS.
 *
 * `apiClient.get<T>()` espone `res.data` come `T` (default `any`). Senza un tipo
 * di risposta esplicito ogni `useState` del modale catalogo riceve dati non
 * verificati. Questi tipi sono la fonte canonica della forma consumata dal
 * frontend per:
 *   - GET /pos/products                       → PosCatalogProductListResponse
 *   - GET /pos/products/:id/supplier-skus      → PosProductSupplierSku[]
 *   - GET /pos/products/:id/serials           → PosProductSerialRow[]
 *
 * I campi noti sono tipizzati esplicitamente; sulla riga prodotto resta
 * l'index signature `unknown` (NON `any`) per i numerosi campi calcolati lato
 * SQL (def_*, totali stock, price_lists_canonical, …) senza vincolare la lista
 * alla shape fluttuante.
 */

/** Livello competenza richiesto su un prodotto (mirror del SoT frontend). */
export type PosSkillLevel = 'base' | 'intermedio' | 'avanzato' | 'esperto';

/** Tag competenza aggregato (`skills_by_product` in listProducts). */
export interface PosCatalogProductSkillTag {
  skill_id: string;
  name: string;
  color?: string | null;
  min_skill_level?: PosSkillLevel | string | null;
}

/** Riga prodotto del listing `/pos/products` (campi noti + calcolati). */
export const posCatalogProductListItemSchema = z.object({
  id: z.string(),
  name: z.string().nullable().optional(),
  sku: z.string().nullable().optional(),
  /** Codice principale storico e alias normalizzati della stessa anagrafica. */
  ean: z.string().nullable().optional(),
  alternate_eans: z.array(z.string()).optional(),
  eans: z.array(z.string()).optional(),
  description: z.string().nullable().optional(),
  product_nature: z.enum(['product', 'service', 'canvas']).nullable().optional(),
  category_id: z.string().nullable().optional(),
  brand_id: z.string().nullable().optional(),
  unit_of_measure_id: z.string().nullable().optional(),
  supplier_id: z.string().nullable().optional(),
  product_type_id: z.string().nullable().optional(),
  product_status: z.enum(['active', 'inactive', 'discontinued', 'draft']).nullable().optional(),
  skills: z.array(z.object({
    skill_id: z.string(), name: z.string(), color: z.string().nullable().optional(),
    min_skill_level: z.string().nullable().optional(),
  }).passthrough()).nullable().optional(),
  category_name: z.string().nullable().optional(),
  product_type_name: z.string().nullable().optional(),
  brand_name: z.string().nullable().optional(),
  vat_rate: z.union([z.number(), z.string()]).nullable().optional(),
  is_locked: z.boolean().optional(),
  managed_by_org_id: z.string().uuid().nullable().optional(),
  /** true se l'articolo ha un prezzo in almeno un listino non archiviato e in validità. */
  has_valid_price_list: z.boolean().optional(),
}).passthrough();
export type PosCatalogProductListItem = z.infer<typeof posCatalogProductListItemSchema>;

/** Paginazione canonica (mirror di `paginatedResponse`). */
export const posCatalogPaginationSchema = z.object({
  page: z.number(), limit: z.number(), total: z.number(), totalPages: z.number(),
}).passthrough();
export type PosCatalogPagination = z.infer<typeof posCatalogPaginationSchema>;

/** Payload `data` di `/pos/products` (paginatedResponse). */
export const posCatalogProductListResponseSchema = z.object({
  items: z.array(posCatalogProductListItemSchema),
  pagination: posCatalogPaginationSchema,
}).passthrough();
export type PosCatalogProductListResponse = z.infer<typeof posCatalogProductListResponseSchema>;

/** Canonical category/brand option returned by catalog administrative routes. */
export const posCatalogNamedOptionSchema = z.object({
  id: z.string(),
  name: z.string(),
  is_active: z.boolean().nullable().optional(),
  is_locked: z.boolean().optional(),
  managed_by_org_id: z.string().uuid().nullable().optional(),
}).strip();

/** Riga mapping SKU fornitore di `/pos/products/:id/supplier-skus`. */
export const posProductSupplierSkuSchema = z.object({
  id: z.string(),
  canonical_product_id: z.string().optional(),
  is_historical: z.boolean().optional(),
  product_id: z.string(),
  variant_id: z.string().nullable().optional(),
  variant_sku: z.string().nullable().optional(),
  variant_ean: z.string().nullable().optional(),
  variant_attributes: z.record(z.string(), z.string()).nullable().optional(),
  supplier_id: z.string(),
  supplier_sku: z.string().trim().min(1),
  supplier_name: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
}).strict();
export type PosProductSupplierSku = z.infer<typeof posProductSupplierSkuSchema>;

export const posProductSupplierSkuListSchema = z.array(posProductSupplierSkuSchema);

/** Il CRUD per prodotto restituisce sempre una lista, anche quando è vuota. */
export type PosProductSupplierSkuListResponse = z.infer<typeof posProductSupplierSkuListSchema>;

/** Lookup batch SKU fornitore indicizzato per prodotto. */
export const posSupplierSkuLookupResponseSchema = z.record(z.string(), z.string());
export type PosSupplierSkuLookupResponse = z.infer<typeof posSupplierSkuLookupResponseSchema>;

/** Riga seriale flat di `/pos/products/:id/serials`. */
export interface PosProductSerialRow {
  id: string;
  serial_number: string;
  status: string;
  warehouse_name?: string | null;
  lot_number?: string | null;
  unit_cost?: string | number | null;
  last_movement_id?: string | null;
  movement_reference_type?: string | null;
  movement_reference_id?: string | null;
  movement_type?: string | null;
  movement_created_at?: string | null;
}

/** Optional for existing receipt/transfer consumers, explicit for procurement. */
export const posDocumentScanPolicySchema = z.enum([
  'purchase_order', 'active_ddt_unvalued', 'active_ddt_valued',
]);
export type PosDocumentScanPolicy = z.infer<typeof posDocumentScanPolicySchema>;

/**
 * Query canonica del lookup prodotto riusata da picker e scanner. In `exact`
 * il server restituisce tutte le identità esatte: `limit` non può nascondere
 * una collisione tra articoli.
 */
export const PosProductSearchQuerySchema = z.object({
  q: z.string().max(1024).optional(),
  limit: z.coerce.number().int().positive().max(100).optional(),
  nature: z.enum(['product', 'service', 'canvas']).optional(),
  excludeServices: z.enum(['true', 'false', '1', '0']).transform((v) => v === 'true' || v === '1').optional(),
  includeSerials: z.enum(['true', 'false', '1', '0']).transform((v) => v === 'true' || v === '1').optional(),
  physicalOnly: z.enum(['true', 'false', '1', '0']).transform((v) => v === 'true' || v === '1').optional(),
  warehouse_id: z.string().uuid().optional(),
  supplier_id: z.string().uuid().optional(),
  /** Commercial context for documents that can select more than one supplier. */
  supplier_ids: z.string()
    .transform((value) => value.split(',').map((id) => id.trim()).filter(Boolean))
    .pipe(z.array(z.string().uuid()).min(1).max(100))
    .optional(),
  /** Leaves legacy catalogue consumers unchanged when omitted. */
  document_policy: posDocumentScanPolicySchema.optional(),
  scope: z.enum(['sku', 'description', 'all']).optional(),
  /** In catalog scope supplier-SKU matching is constrained to `supplier_id`. */
  supplier_scope: z.enum(['catalog']).optional(),
  exact: z.enum(['true', 'false', '1', '0']).transform((v) => v === 'true' || v === '1').optional(),
}).strict();

/** Selectable skill in the canonical Catalogo option loader. */
export const posCatalogSkillOptionSchema = z.object({
  id: z.string(),
  name: z.string(),
  color: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  is_active: z.boolean().optional(),
}).strip();

/** Selectable product type in the canonical Catalogo option loader. */
export const posCatalogProductTypeOptionSchema = z.object({
  id: z.string(),
  name: z.string(),
  category_id: z.string().nullable().optional(),
  is_active: z.boolean().nullable().optional(),
}).strip();

export type PosCatalogUnitOption = z.infer<typeof posCatalogUnitOptionSchema>;

/** Canonical unit option returned by the catalog administrative route. */
export const posCatalogUnitOptionSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
}).strip();

export type PosCatalogSkillOption = z.infer<typeof posCatalogSkillOptionSchema>;

export type PosProductMutationResponse = z.infer<typeof posProductMutationResponseSchema>;

export type PosProductSearchResponse = PosProductSearchResult[];

/**
 * Canonical identity projection returned by the unified product lookup.
 *
 * Search consumers must treat every identifier as an alias of `id`: an EAN
 * (including an alternate EAN), internal SKU, supplier SKU, variant or serial
 * never creates a second product identity.  The optional fields keep the
 * projection forwards-compatible with endpoints that can resolve variants or
 * registered serial units.
 * After a logical merge, `id`/`variant_id` are current selection identities;
 * `original_*` records the precise alias or serial origin. A retained distinct
 * variant can still have its original UUID under a canonical parent.
 */
export const posProductSearchResultSchema = z.object({
  has_variants: z.boolean().optional(),
  variant_attributes: z.record(z.string()).nullable().optional(),
  id: z.string(),
  original_product_id: z.string().optional(),
  canonical_product_id: z.string().optional(),
  original_variant_id: z.string().nullable().optional(),
  canonical_variant_id: z.string().nullable().optional(),
  name: z.string(),
  sku: z.string().nullable().optional(),
  ean: z.string().nullable().optional(),
  alternate_eans: z.array(z.string()).optional(),
  eans: z.array(z.string()).optional(),
  supplier_sku: z.string().nullable().optional(),
  price: z.union([z.number(), z.string()]).nullable().optional(),
  list_price: z.union([z.number(), z.string()]).nullable().optional(),
  vat_rate: z.union([z.number(), z.string()]).nullable().optional(),
  /** Faccia SdI di catalogo (solo a 0%): natura e riferimento di default
   *  dell'aliquota; l'override di listino/testata è risolto dal server. */
  vat_nature: z.string().nullable().optional(),
  riferimento_normativo: z.string().nullable().optional(),
  unit_of_measure_id: z.string().nullable().optional(),
  product_nature: z.string().nullable().optional(),
  serial_mode: z.enum(['unique', 'non_unique', 'none']).nullable().optional(),
  unit_of_measure_name: z.string().nullable().optional(),
  total_stock: z.union([z.number(), z.string()]).nullable().optional(),
  warehouse_stock: z.union([z.number(), z.string()]).nullable().optional(),
  variant_id: z.string().nullable().optional(),
  variant_sku: z.string().nullable().optional(),
  variant_ean: z.string().nullable().optional(),
  serial_number: z.string().nullable().optional(),
  serial_id: z.string().nullable().optional(),
  unit_id: z.string().nullable().optional(),
  /**
   * Conserva l'identità che ha generato il risultato: nessun consumer può
   * dedurre una precedenza tra SKU, EAN, variante o seriale.
   */
  match_type: z.enum([
    'product_sku', 'primary_ean', 'alternate_ean', 'variant_sku',
    'variant_ean', 'supplier_sku', 'serial', 'product_name',
  ]).nullable().optional(),
  matched_code: z.string().nullable().optional(),
  /** Supplier that produced a supplier_sku identity match (never inferred). */
  matched_supplier_id: z.string().nullable().optional(),
}).strict();

/** Concrete identity that makes a newly submitted EAN ambiguous. */
export const posProductEanCollisionSchema = z.object({
  ean: z.string(),
  product_id: z.string(),
  product_name: z.string(),
  identity_type: z.enum(['primary_ean', 'alternate_ean', 'variant_ean']),
  variant_id: z.string().nullable(),
});

/** Identity returned by the exact serial lookup endpoint. */
export const posProductSerialLookupSchema = z.object({
  serial_id: z.string(),
  serial_number: z.string(),
  kind: z.string().optional(),
  serial_mode: z.enum(['unique', 'non_unique', 'none']),
  status: z.string(),
  warehouse_id: z.string().nullable(),
  lot_number: z.string().nullable().optional(),
  /** Physical supplier provenance from the serial unit's lot, never inferred from an internal SKU. */
  supplier_id: z.string().nullable().optional(),
  supplier_sku: z.string().nullable().optional(),
  unit_id: z.string(),
  product_id: z.string(),
  variant_id: z.string().nullable().optional(),
}).strict();

/** Query canonica dell'identificatore seriale esatto. */
export const PosProductSerialLookupQuerySchema = z.object({
  code: z.string().max(1024).optional(),
  warehouse_id: z.string().uuid().optional(),
  /** Explicit choice among units sharing a code; never substitutes for code/tenant scope. */
  serial_id: z.string().uuid().optional(),
}).strict();

export type PosProductSerialLookup = z.infer<typeof posProductSerialLookupSchema>;

/**
 * Canonical product identity returned after create/update.  Linked UI flows
 * use this DTO instead of treating the write response as an untyped object.
 */
export const posProductMutationResponseSchema = z.object({
  id: z.string(),
  original_product_id: z.string().optional(),
  canonical_product_id: z.string().optional(),
  original_variant_id: z.string().nullable().optional(),
  canonical_variant_id: z.string().nullable().optional(),
  member_product_ids: z.array(z.string()).optional(),
  is_merged: z.boolean().optional(),
  name: z.string().nullable().optional(),
  sku: z.string().nullable().optional(),
  ean: z.string().nullable().optional(),
  alternate_eans: z.array(z.string()).optional(),
  eans: z.array(z.string()).optional(),
  /**
   * Terna fiscale dell'articolo (stesso modello di ordine e DDT): aliquota
   * numerica del trattamento risolto + natura/riferimento persistiti (solo a
   * 0%). `vat_rate_id` è il trattamento interno risolto dal service.
   */
  vat_rate: z.union([z.number(), z.string()]).nullable().optional(),
  vat_rate_id: z.string().nullable().optional(),
  vat_code: z.string().nullable().optional(),
  vat_nature: z.string().nullable().optional(),
  riferimento_normativo: z.string().nullable().optional(),
  product_nature: z.enum(['product', 'service', 'canvas']).nullable().optional(),
  serial_mode: z.enum(['unique', 'non_unique', 'none']).nullable().optional(),
}).strip();

export type PosProductEanCollision = z.infer<typeof posProductEanCollisionSchema>;

export type PosCatalogProductTypeOption = z.infer<typeof posCatalogProductTypeOptionSchema>;

export const posProductSerialLookupResponseSchema = z.object({
  product: posProductSearchResultSchema,
  serial: posProductSerialLookupSchema,
}).strict();

export type PosProductSearchQuery = z.infer<typeof PosProductSearchQuerySchema>;

export type PosProductSerialLookupQuery = z.infer<typeof PosProductSerialLookupQuerySchema>;

export type PosCatalogNamedOption = z.infer<typeof posCatalogNamedOptionSchema>;

export type PosProductSearchResult = z.infer<typeof posProductSearchResultSchema>;

export type PosProductSerialLookupResponse = z.infer<typeof posProductSerialLookupResponseSchema>;
